// /resources/js/app/modules/MonitoringManager.js
import { TelegramClient } from 'telegram';
import { StringSession } from 'telegram/sessions';
import { ProxySocket } from '../../network/ProxySocket.js';
import { ContractParser } from '../../utils/ContractParser.js';
import { StateManager } from '../core/StateManager.js';
import { DomElements } from '../core/DomElements.js';
import { chainDetector } from '../../utils/ChainDetector.js';
import { eventBus } from '../core/EventBus.js';
import { toast } from '../core/Toast.js';
import axios from 'axios';
import { optimisticSignalBuilder } from './v2/OptimisticSignalBuilder.js';

class MonitoringManager {
    constructor() {
        this.client = null;
        this.submittedCallsCache = new Set();
        this.isMonitoringActive = false;
        this.sessionCallCount = 0;
        console.log('[MonitoringManager] Instance created.');
    }

    _updatePillUI(state) {
        // Find V2 elements by their ID directly, making this method compatible.
        const pill = document.getElementById('monitoring-control-pill');
        if (!pill) return;

        const dot = pill.querySelector('#monitoring-status-dot');
        const text = pill.querySelector('#monitoring-status-text');
        const counter = pill.querySelector('#monitoring-call-counter');
        const tgIcon = pill.querySelector('#monitoring-tg-icon');

        if (!dot || !text || !counter) return;

        dot.classList.remove('bg-text-dark', 'bg-yellow-500', 'bg-green-500', 'animate-pulse');
        text.classList.remove('text-primary'); // Reset color class
        tgIcon.classList.add('hidden');

        switch (state) {
            case 'unconnected':
                dot.classList.add('bg-text-dark');
                text.textContent = 'CONNECT & EARN';
                tgIcon.classList.remove('hidden');
                text.classList.add('text-primary');
                break;
            case 'offline':
                dot.classList.add('bg-red-500');
                text.textContent = 'MONITORING OFFLINE';
                tgIcon.classList.remove('hidden');
                counter.classList.add('hidden');
                break;
            case 'connecting':
                dot.classList.remove('bg-red-500', 'bg-green-500'); // <-- ADD THIS LINE
                dot.classList.add('bg-yellow-500', 'animate-pulse');
                tgIcon.classList.remove('hidden');
                text.textContent = 'CONNECTING...';
                break;
            case 'online':
                dot.classList.remove('bg-red-500'); // <-- ADD THIS LINE
                dot.classList.add('bg-green-500', 'animate-pulse');
                tgIcon.classList.remove('hidden');
                text.textContent = 'MONITORING LIVE';
                counter.textContent = `(${this.sessionCallCount})`;
                counter.classList.toggle('hidden', this.sessionCallCount === 0);
                break;
        }
    }
    
    // --- START: SIMPLIFIED INIT METHOD ---
    init() {
        // The init method no longer manipulates the UI. It's now the responsibility
        // of the page controller (V1 or V2) to set the initial pill state.
        console.log('[MonitoringManager] Service initialized.');
    }
    // --- END: SIMPLIFIED INIT METHOD ---

    toggle() {
        if (this.isMonitoringActive) {
            this.stop();
        } else {
            this.start();
        }
    }

    async start() {
        if (this.isMonitoringActive) {
            console.warn('[MonitoringManager] Start called but monitoring is already active.');
            return;
        }

        console.log('[MonitoringManager.start] Attempting to start monitoring...');
        this.sessionCallCount = 0;
        this._updatePillUI('connecting');
        const sgnlUser = window.SGNL.user;
        const config = window.SGNL.config;
        const sessionKey = `gramjs_session_user_${sgnlUser.id}`;
        const tgSessionString = localStorage.getItem(sessionKey) || '';
        
        if (!sgnlUser || !sgnlUser.id) {
            this._updatePillUI('offline');
            return;
        }
        try {
            // This listener responds to GramJS's request for a nonce. It must be
            // added *before* the TelegramClient is instantiated.
            window.addEventListener('gram-js-csp-nonce', (event) => {
                const nonce = window.SGNL?.config?.cspNonce;
                if (nonce) {
                    event.detail.nonce = nonce;
                    console.log('[MonitoringManager] Provided CSP nonce to GramJS worker.');
                }
            }, { once: true }); // { once: true } is a performance optimization.
            // --- START: ROBUST CONFIG VALIDATION ---
            const { apiId, apiHash } = config;

            if (!apiId || !apiHash) {
                // This throws a user-friendly error before the library throws a cryptic one.
                throw new Error("Telegram API credentials are not configured. Cannot connect.");
            }

            const stringSession = new StringSession(tgSessionString);
            this.client = new TelegramClient(stringSession, Number(apiId), apiHash, {
                networkSocket: ProxySocket,
                connectionRetries: 5,
                useWSS: true,
            });
            // --- END: ROBUST CONFIG VALIDATION ---

            this.client.addEventHandler((e) => this.handleNewMessage(e), new TelegramClient.events.NewMessage({}));
            
            await this.client.start({ botAuthToken: '' });
            
            this.isMonitoringActive = true;
            this._updatePillUI('online');
        } catch (err) {
            console.error('[MonitoringManager.start] An error occurred during the connection process.', err);

            let userMessage = 'Monitoring error. Please try again.';
            if (err.message && (err.message.includes('AUTH_KEY_UNREGISTERED') || err.message.includes('SESSION_REVOKED') || err.message.includes('BotToken is required'))) {
                userMessage = `Your Telegram session has expired. <a href="/telegram-connect" class="font-bold underline hover:text-white">Click here to reconnect.</a>`;
            } else if (err.message) {
                userMessage = `Monitoring error: ${err.message}`;
            }
            toast.show(userMessage, 'error', 0);
            this.isMonitoringActive = false;
            this._updatePillUI('offline');
        }
    }
    
    stop() {
        if (this.client) {
            this.client.disconnect();
            this.client = null;
        }
        this.isMonitoringActive = false;
        this._updatePillUI('offline');
    }
    async handleNewMessage(event) {
        const message = event.message;
        if (!message?.chat?.title) return;
        
        const parsedContracts = ContractParser.parse(message.message);
        if (parsedContracts.length === 0) return;

        const authorId = message.senderId.toString();
        const authorUsername = event.sender?.username || event.sender?.firstName || `User_${authorId.slice(-4)}`;
        const channelName = message.chat?.title || 'Unknown Channel';
        const rawChatId = message.chatId?.toString();
        const normalizedChatId = rawChatId.startsWith('-100') ? rawChatId.substring(4) : rawChatId;
        const topicId = message.replyTo?.replyToMsgId?.toString() || '1';

        const compositeSourceId = `tg:${normalizedChatId}:${topicId}:${authorId}`;
        const compositeSourceName = `${authorUsername} in ${channelName}`;

        for (const parsedContract of parsedContracts) {
            const submissionKey = `${parsedContract.address}:${compositeSourceId}`;
            if (this.submittedCallsCache.has(submissionKey)) continue;
            this.submittedCallsCache.add(submissionKey);
            
            // Step 1: Perform the SINGLE intelligent lookup for token/pair data
            let tokenData = null; 
            try {
                const tokenApiUrl = `https://api.dexscreener.com/latest/dex/tokens/${parsedContract.address}`;
                const tokenResponse = await axios.get(tokenApiUrl);
                tokenData = tokenResponse.data.pairs?.[0];

                if (!tokenData) {
                    console.log(`[MonitoringManager] Address ${parsedContract.address} not found as token, trying as pair...`);
                    const detectedChain = await chainDetector.detectEVMChain(parsedContract.address) || 'solana';
                    const pairApiUrl = `https://api.dexscreener.com/latest/dex/pairs/${detectedChain}/${parsedContract.address}`;
                    const pairResponse = await axios.get(pairApiUrl);
                    tokenData = pairResponse.data.pairs?.[0];
                }
            } catch (e) {
                console.error(`[MonitoringManager] DexScreener lookup failed for ${parsedContract.address}`, e.message);
                continue; 
            }

            if (!tokenData) {
                 console.warn(`[MonitoringManager] Could not identify a valid token or pair for ${parsedContract.address}. Skipping.`);
                 continue;
            }

            // Step 2: Prepare data for both consumers from the single lookup result
            const finalChain = tokenData.chainId;
            const finalTokenAddress = tokenData.baseToken.address;

            const rawCallDataForApi = {
                sourceId: compositeSourceId,
                sourceName: compositeSourceName,
                messageId: message.id.toString(),
                timestamp: message.date,
            };

            const tokenDataForApi = {
                chain: finalChain,
                address: finalTokenAddress,
            };
            
            // Step 3: Pass the result to both consumers
            this.submitCallToBackend(tokenDataForApi, rawCallDataForApi);

            const rawCallForBuilder = {
                chain: finalChain, 
                sourceId: compositeSourceId,
                sourceName: rawCallDataForApi.sourceName,
                timestamp: rawCallDataForApi.timestamp
            };

            // Pass the already-fetched tokenData directly to the builder
            const optimisticSignalV2 = await optimisticSignalBuilder.create(rawCallForBuilder, tokenData);
            
            if (optimisticSignalV2) {
                window.dispatchEvent(new CustomEvent('v2-optimistic-signal-created', { detail: optimisticSignalV2 }));
            }
        }
    }

    
    submitCallToBackend(tokenData, callData) {
        axios.post('/api/calls/submit', { tokenData, callData }, { withCredentials: true })
            .then(() => console.log(`[MonitoringManager] API submission successful for: ${tokenData.address}`))
            .catch(error => console.error(`[MonitoringManager] Error submitting call:`, error.response?.data || error.message));
    }
}

export const monitoringManager = new MonitoringManager();